import { getIn } from 'formik';
import { forEach } from 'lodash';
import React, { useContext } from 'react';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { getNullSelectOption, SelectOptions } from '../../../../../shared/form/inputs/Select';
import { SelectField } from '../../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { Td, Tr, THead, TBody } from '../../../../../shared/Table';
import {
  GroupedTh,
  GroupedThBorderLeft,
  GroupedThBorderRight,
  GroupingTh,
  TdBorderLeft,
  TdBorderRight,
} from '../../../../../shared/SplitTable';
import { DesignFormDefaultsContext } from '../../../../designs/DesignFormDefaultsContext';
import { FloorCovering } from '../../GetDataForQuoteOrDesignForm';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { roomFieldName } from '../../quoteOrDesignFormFieldNameHelpers';
import { getDefaultMaximumTemperatureInDegreesCelsius } from '../../rooms/designRoomDetailsFormModel';
import { DownlossTypeCode, downlossTypeCodeSelectOptions } from '../../rooms/heatLoss/downlossTypeCode';
import { defaultTemperatureBelowInDegreesCelsiusByDownlossTypeCode } from '../../rooms/heatLoss/roomHeatLossDefaults';
import { RoomFormModel } from '../../rooms/roomFormModel';
import { UnitFormModel } from '../unitFormModel';
import { RoomsHeatLossInputField } from './RoomsHeatLossInputField';
import { RoomsHeatLossTableColumnHeaderContextMenu } from './RoomsHeatLossTableColumnHeaderContextMenu';

type Props = {
  unit: UnitFormModel;
  unitIndex: number;
  filteredManifoldId: string | null;
  fillDownColumn: (columnFieldName: string) => () => void;
  roomHeatLossDetailsFieldName: (manifoldIndex: number, roomIndex: number, fieldName: string) => string;
  getRoomsToFillDetails: () => undefined | Array<{ manifoldIndex: number; room: RoomFormModel; roomIndex: number }>;
};

export const RoomsFloorTable = ({
  unit,
  unitIndex,
  filteredManifoldId,
  fillDownColumn,
  roomHeatLossDetailsFieldName,
  getRoomsToFillDetails,
}: Props) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { setFieldValue } = formContext.formProps;
  const floorElements = assertNotNull(unit.heatLossDetails).floorElements;
  const { floorCoveringsByName } = formContext.referenceData;

  const { unitFloorElements, setUnitFloorElements } = assertNotNull(useContext(DesignFormDefaultsContext));

  const roomFloorHeatLossDetailsFieldName = (
    manifoldIndex: number,
    roomIndex: number,
    floorElementIndex: number,
    fieldName: string,
  ) => roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, `floors.${floorElementIndex}.${fieldName}`);
  const fillDownColumns = (fieldNames: Array<string>) => () => {
    fieldNames.forEach(fieldName => fillDownColumn(fieldName)());
  };

  const fillDownFloorCoveringType = () => {
    fillDownColumns([
      'floorCoveringName',
      'floorCoveringThermalResistancePerMillimetre',
      'floorCoveringThicknessInMillimetres',
      'underlayThermalResistancePerMillimetre',
      'underlayThicknessInMillimetres',
    ])();

    const roomsToFillDetails = getRoomsToFillDetails();

    if (roomsToFillDetails === undefined || roomsToFillDetails.length === 0) {
      return;
    }

    const floorCoveringName = getIn(roomsToFillDetails[0].room, 'heatLossDetails.floorCoveringName');
    const floorCovering: FloorCovering | undefined = floorCoveringsByName[floorCoveringName];

    forEach(roomsToFillDetails, ({ manifoldIndex, roomIndex }) => {
      setMaximumTemperatureToDefault(manifoldIndex, roomIndex, floorCovering);
      setNumberOfFloorSensorsToDefault(manifoldIndex, roomIndex, floorCovering);
    });
  };

  const onDownlossTypeChange = (
    manifoldIndex: number,
    roomIndex: number,
    floorElementIndex: number,
    newDownlossTypeCode: DownlossTypeCode | null,
  ) => {
    if (newDownlossTypeCode == null) {
      return;
    }

    const newTemperatureBelow = defaultTemperatureBelowInDegreesCelsiusByDownlossTypeCode[
      newDownlossTypeCode
    ].toString();

    setFieldValue(
      roomFloorHeatLossDetailsFieldName(
        manifoldIndex,
        roomIndex,
        floorElementIndex,
        'temperatureBelowInDegreesCelsius',
      ),
      newTemperatureBelow,
    );

    if (unitFloorElements) {
      const newUnitFloorElements = [...unitFloorElements];
      newUnitFloorElements[floorElementIndex].manifoldRoomValues[manifoldIndex][roomIndex] = {
        temperature: newTemperatureBelow,
        downlossTypeCode: newDownlossTypeCode,
      };

      setUnitFloorElements(newUnitFloorElements);
    }
  };

  const onTemperatureBelowChange = (
    manifoldIndex: number,
    roomIndex: number,
    floorElementIndex: number,
    newTemperatureBelow: string,
  ) => {
    if (unitFloorElements) {
      const newUnitFloorElements = [...unitFloorElements];
      newUnitFloorElements[floorElementIndex].manifoldRoomValues[manifoldIndex][
        roomIndex
      ].temperature = newTemperatureBelow;
      setUnitFloorElements(newUnitFloorElements);
    }
  };

  const onSelectFloorCovering = (manifoldIndex: number, roomIndex: number, newFloorCoveringName: string | null) => {
    if (newFloorCoveringName == 'Other' || newFloorCoveringName == null) {
      return;
    }

    const floorCovering: FloorCovering | undefined = floorCoveringsByName[newFloorCoveringName];

    if (floorCovering == null) {
      return;
    }

    setFieldValue(
      roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'floorCoveringThermalResistancePerMillimetre'),
      floorCovering.defaultThermalResistancePerMillimetre?.toString() ?? '',
    );

    setFieldValue(
      roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'floorCoveringThicknessInMillimetres'),
      floorCovering.defaultThicknessInMillimetres?.toString() ?? '',
    );

    setMaximumTemperatureToDefault(manifoldIndex, roomIndex, floorCovering);

    setFieldValue(
      roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'underlayThermalResistancePerMillimetre'),
      floorCovering.defaultUnderlayThermalResistancePerMillimetre?.toString() ?? '',
    );

    setFieldValue(
      roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'underlayThicknessInMillimetres'),
      floorCovering.defaultUnderlayThicknessInMillimetres?.toString() ?? '',
    );

    setNumberOfFloorSensorsToDefault(manifoldIndex, roomIndex, floorCovering);
  };

  const setMaximumTemperatureToDefault = (
    manifoldIndex: number,
    roomIndex: number,
    floorCovering: FloorCovering | undefined,
  ) => {
    const defaultMaximumTemperature = getDefaultMaximumTemperatureInDegreesCelsius(
      unit.manifolds[manifoldIndex].rooms?.[roomIndex].roomType,
      floorCovering,
    );

    setFieldValue(
      roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.maximumTemperatureInDegreesCelsius'),
      defaultMaximumTemperature,
    );
  };

  const setNumberOfFloorSensorsToDefault = (
    manifoldIndex: number,
    roomIndex: number,
    floorCovering: FloorCovering | undefined,
  ) => {
    const defaultNumberOfFloorSensors = floorCovering?.isSensitive ? '1' : '0';

    setFieldValue(
      roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfFloorSensors'),
      defaultNumberOfFloorSensors,
    );
  };

  const floorCoveringOptions: SelectOptions<string | null> = [
    getNullSelectOption<string>(),
    ...Object.keys(floorCoveringsByName).map(name => ({ text: name, value: name })),
  ];

  return (
    <>
      <THead>
        <Tr>
          {floorElements.map((floorElement, index) => (
            <GroupingTh key={index} colSpan={3}>
              {floorElement.name || 'Unnamed Floor Element'}
            </GroupingTh>
          ))}
          <GroupingTh colSpan={3}>Floor covering</GroupingTh>
          <GroupingTh colSpan={2}>Decking</GroupingTh>
          <GroupingTh colSpan={2}>Underlay / Adhesive</GroupingTh>
        </Tr>
        <Tr>
          {floorElements.map((floorElement, index) => (
            <React.Fragment key={index}>
              <GroupedThBorderLeft>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`floors.${index}.roomAreaPercentage`)}
                >
                  % of Room Area
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedThBorderLeft>
              <GroupedTh>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumns([
                    `floors.${index}.downlossTypeCode`,
                    `floors.${index}.temperatureBelowInDegreesCelsius`,
                  ])}
                >
                  Downloss Type
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedTh>
              <GroupedThBorderRight>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`floors.${index}.temperatureBelowInDegreesCelsius`)}
                >
                  Temp. Below (°C)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedThBorderRight>
            </React.Fragment>
          ))}
          <GroupedThBorderLeft>
            <RoomsHeatLossTableColumnHeaderContextMenu fillDownColumn={fillDownFloorCoveringType}>
              Type
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </GroupedThBorderLeft>
          <GroupedTh>
            <RoomsHeatLossTableColumnHeaderContextMenu
              fillDownColumn={fillDownColumn('floorCoveringThermalResistancePerMillimetre')}
            >
              R-Value per mm
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </GroupedTh>
          <GroupedThBorderRight>
            <RoomsHeatLossTableColumnHeaderContextMenu
              fillDownColumn={fillDownColumn('floorCoveringThicknessInMillimetres')}
            >
              Thickness (mm)
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </GroupedThBorderRight>
          <GroupedThBorderLeft>
            <RoomsHeatLossTableColumnHeaderContextMenu
              fillDownColumn={fillDownColumn('deckingThermalResistancePerMillimetre')}
            >
              R-Value per mm
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </GroupedThBorderLeft>
          <GroupedThBorderRight>
            <RoomsHeatLossTableColumnHeaderContextMenu fillDownColumn={fillDownColumn('deckingThicknessInMillimetres')}>
              Thickness (mm)
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </GroupedThBorderRight>
          <GroupedThBorderLeft>
            <RoomsHeatLossTableColumnHeaderContextMenu
              fillDownColumn={fillDownColumn('underlayThermalResistancePerMillimetre')}
            >
              R-Value per mm
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </GroupedThBorderLeft>
          <GroupedThBorderRight>
            <RoomsHeatLossTableColumnHeaderContextMenu
              fillDownColumn={fillDownColumn('underlayThicknessInMillimetres')}
            >
              Thickness (mm)
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </GroupedThBorderRight>
        </Tr>
      </THead>
      <TBody>
        {unit.manifolds
          .map((manifold, manifoldIndex) => ({ manifold, manifoldIndex }))
          .filter(({ manifold }) => filteredManifoldId === null || manifold.id === filteredManifoldId)
          .map(({ manifold, manifoldIndex }) =>
            manifold.rooms == null
              ? null
              : manifold.rooms.map((room, roomIndex) => (
                  <Tr key={`${manifoldIndex}-${roomIndex}`}>
                    {floorElements.map((floorElement, floorElementIndex) => (
                      <React.Fragment key={floorElementIndex}>
                        <TdBorderLeft>
                          <TextInputField
                            name={roomFloorHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              floorElementIndex,
                              'roomAreaPercentage',
                            )}
                            label={null}
                          />
                        </TdBorderLeft>
                        <Td>
                          <SelectField<DownlossTypeCode | null>
                            name={roomFloorHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              floorElementIndex,
                              'downlossTypeCode',
                            )}
                            label={null}
                            options={downlossTypeCodeSelectOptions}
                            onChange={newValue =>
                              onDownlossTypeChange(manifoldIndex, roomIndex, floorElementIndex, newValue)
                            }
                          />
                        </Td>
                        <TdBorderRight>
                          <RoomsHeatLossInputField
                            name={roomFloorHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              floorElementIndex,
                              'temperatureBelowInDegreesCelsius',
                            )}
                            referenceFieldName={roomFloorHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              floorElementIndex,
                              'roomAreaPercentage',
                            )}
                            label={null}
                            onChange={newValue =>
                              onTemperatureBelowChange(manifoldIndex, roomIndex, floorElementIndex, newValue)
                            }
                          />
                        </TdBorderRight>
                      </React.Fragment>
                    ))}
                    <TdBorderLeft>
                      <SelectField<string | null>
                        name={roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'floorCoveringName')}
                        label={null}
                        options={floorCoveringOptions}
                        onChange={newValue => onSelectFloorCovering(manifoldIndex, roomIndex, newValue)}
                      />
                    </TdBorderLeft>
                    <Td>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(
                          manifoldIndex,
                          roomIndex,
                          'floorCoveringThermalResistancePerMillimetre',
                        )}
                        label={null}
                      />
                    </Td>
                    <TdBorderRight>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(
                          manifoldIndex,
                          roomIndex,
                          'floorCoveringThicknessInMillimetres',
                        )}
                        label={null}
                      />
                    </TdBorderRight>
                    <TdBorderLeft>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(
                          manifoldIndex,
                          roomIndex,
                          'deckingThermalResistancePerMillimetre',
                        )}
                        label={null}
                      />
                    </TdBorderLeft>
                    <TdBorderRight>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'deckingThicknessInMillimetres')}
                        label={null}
                      />
                    </TdBorderRight>
                    <TdBorderLeft>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(
                          manifoldIndex,
                          roomIndex,
                          'underlayThermalResistancePerMillimetre',
                        )}
                        label={null}
                      />
                    </TdBorderLeft>
                    <TdBorderRight>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'underlayThicknessInMillimetres')}
                        label={null}
                      />
                    </TdBorderRight>
                  </Tr>
                )),
          )}
      </TBody>
    </>
  );
};
